// Dashboard.
//
// Two distinct experiences inside one page:
//
//  - LOGGED-OUT VISITORS: marketing hero (light/dark adaptive cyan
//    abyss) + trust badges + game showcase (frame-only, no flat tile
//    buttons) + bottom CTA strip.
//  - LOGGED-IN PLAYERS: player header chip + wallet hero with quick
//    actions + featured "win prizes" banner + the real game grid that
//    launches matches.
//
// Game-specific lobbies / match flows live elsewhere — this page is
// intentionally just "front door" surfaces.
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@baaligames/shared-ui/lib/auth';
import { refreshWallet } from '@baaligames/shared-ui/lib/wallet';
import { useT } from '@baaligames/shared-ui/lib/i18n';
import { useToast } from '@baaligames/shared-ui/lib/toast';
import { api, ApiError } from '@baaligames/shared-ui/lib/api';
import LockedGameModal from '../components/LockedGameModal';
import { useLobbyGames, type LobbyGame } from '../lib/lobbyGames';
import LiveOddsRow from '../components/CricketOddsRow';
import TopGamesCarousel from '../components/TopGamesCarousel';
import { useNavigate } from 'react-router-dom';
import Icon from '@baaligames/shared-ui/components/Icon';
import OfferHero, { pickHeroOffer } from '@baaligames/shared-ui/components/OfferHero';
import type { OfferLike } from '@baaligames/shared-ui/components/OfferCard';
import { GAMES } from '../games/registry';

// The five Top Games, in row order. Shared by the tile card and by the pointer
// that hangs beneath it (the pointer positions itself at (index + 0.5) / 5).
const TOP_GAME_ORDER = ['cricket', 'matka_king', 'football', 'lucky7', 'tennis', 'roulette'];

/* ─── Logged-out blocks ─────────────────────────────────────────── */

function LoggedOutHero() {
  const t = useT();
  return (
    <section className="relative overflow-hidden rounded-3xl border border-arena-200/60 dark:border-neon-400/20 shadow-xl">
      {/* Light-mode gradient — bright cyan ocean. Dark-mode gradient
          replaces it via the .dark variant. Two layers means each
          mode gets its own identity instead of a hardcoded dark slab
          that ignored the theme. */}
      <div
        className="block dark:hidden absolute inset-0"
        style={{
          background:
            'linear-gradient(135deg, #E0F7FA 0%, #8FFFFF 28%, #62F4F7 65%, #23D4D8 100%)',
        }}
        aria-hidden
      />
      <div
        className="hidden dark:block absolute inset-0"
        style={{
          background:
            'linear-gradient(135deg, #0A3C5F 0%, #04192A 60%, #000F1A 100%)',
        }}
        aria-hidden
      />

      {/* Decorative neon spotlights — same in both modes, just feel
          different against the underlying gradient. */}
      <div
        className="absolute -top-16 -right-12 w-48 h-48 rounded-full opacity-60 mix-blend-screen"
        style={{ background: 'radial-gradient(circle, rgba(255,255,255,.55), transparent 65%)' }}
        aria-hidden
      />
      <div
        className="absolute -bottom-20 -left-12 w-56 h-56 rounded-full opacity-60"
        style={{ background: 'radial-gradient(circle, rgba(240,126,51,.4), transparent 65%)' }}
        aria-hidden
      />

      <div className="relative px-5 py-6">
        <div className="text-[10px] tracking-[.32em] uppercase font-bold text-brand-700 dark:text-neon-300">
          {t('home.welcomeTo')}
        </div>

        {/* Title — dark navy in light mode, cyan-glow in dark mode. */}
        <h1
          className="font-display font-black text-3xl sm:text-4xl mt-1
                     bg-clip-text text-transparent
                     bg-[linear-gradient(90deg,#04192A_0%,#0A3C5F_50%,#04192A_100%)]
                     dark:bg-[linear-gradient(90deg,#8FFFFF_0%,#FFFFFF_50%,#8FFFFF_100%)]
                     dark:[text-shadow:0_0_14px_rgba(143,255,254,.45)]"
        >
          BAALI ARENA
        </h1>

        <p className="mt-1 text-sm text-arena-700 dark:text-neon-200/80">
          {t('home.heroSub')}
        </p>

        {/* Action buttons — pill, equal width, equal height, properly
            centred via grid. Solved the previous alignment problem. */}
        <div className="mt-5 grid grid-cols-2 gap-2.5">
          <Link
            to="/register"
            className="inline-flex items-center justify-center gap-1.5 h-11 rounded-full
                       font-bold text-[13px] tracking-wider uppercase text-white
                       bg-gradient-to-b from-brand-400 via-brand-500 to-brand-600
                       border border-white/20 active:scale-[0.98] transition"
            style={{ boxShadow: '0 10px 22px -10px rgba(240,126,51,.7), inset 0 1px 0 rgba(255,255,255,.35)' }}
          >
            <Icon name="rocket_launch" size={18} filled /> {t('common.signUp')}
          </Link>
          <Link
            to="/login"
            className="inline-flex items-center justify-center gap-1.5 h-11 rounded-full
                       font-bold text-[13px] tracking-wider uppercase
                       text-arena-800 dark:text-neon-200
                       bg-white/40 dark:bg-white/5 hover:bg-white/55 dark:hover:bg-white/10
                       border border-arena-700/30 dark:border-neon-300/40
                       backdrop-blur active:scale-[0.98] transition"
          >
            <Icon name="login" size={18} filled /> {t('common.login')}
          </Link>
        </div>
      </div>
    </section>
  );
}

/**
 * RewardsTeaserStrip — anonymous-visitor only.
 *
 * Phase 14: pulls a few currently-active offer artworks from the
 * public endpoint and parades them as a horizontal scroll strip.
 *
 * Image-only by design. No reward amounts, no titles — high-quality
 * 3D PNGs create mystery + visual attraction, and the only way to
 * learn what the reward actually is, is to sign up. The "Sign up to
 * claim" CTA after the strip funnels the curious to /register.
 *
 * Renders nothing if there are no active offers with artwork — so
 * the landing page is never empty.
 */
function RewardsTeaserStrip() {
  const t = useT();
  const [items, setItems] = useState<Array<{ id: number; image_url: string }> | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get<{ offers: Array<{ id: number; image_url: string }> }>(
          '/public/featured-offers'
        );
        if (!cancelled) setItems(r.offers);
      } catch {
        if (!cancelled) setItems([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!items || items.length === 0) return null;

  return (
    // Task F (theme-aware) — was a fixed dark-only section using
    // hardcoded hex `bg-gradient-to-br from-[#0a1626] via-[#0e1d33]
    // to-[#1a0f08]`, which looked the same regardless of the
    // light/dark toggle. Refactored to mirror LoggedOutHero's
    // pattern: two absolute-positioned gradient layers, theme-aware
    // border, and a halo set that uses mix-blend-screen so it
    // composes correctly on both modes.
    <section className="relative overflow-hidden rounded-3xl
                        border border-arena-200/60 dark:border-white/10
                        shadow-[0_20px_60px_-25px_rgba(0,0,0,0.45)]
                        dark:shadow-[0_20px_60px_-25px_rgba(0,0,0,0.65)]">
      {/* Light-mode gradient — cyan-tinted hero wash to match the
          BAALI ARENA card's energy without going as bright. */}
      <div
        className="block dark:hidden absolute inset-0"
        style={{
          background:
            'linear-gradient(135deg, #E0F7FA 0%, #B3E9FF 40%, #FFD9A8 80%, #F4A24A 100%)',
        }}
        aria-hidden
      />
      {/* Dark-mode gradient — preserved the original deep-navy +
          brand-warmth ramp; the original artwork was designed
          against this slab so we're not rebuilding the dark look. */}
      <div
        className="hidden dark:block absolute inset-0"
        style={{
          background:
            'linear-gradient(135deg, #0a1626 0%, #0e1d33 55%, #1a0f08 100%)',
        }}
        aria-hidden
      />
      {/* Background ambient halos — alpha-tinted so they read on
          both modes. mix-blend-screen lets the white core lift on
          light backgrounds and stay luminous on dark. */}
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute -top-1/3 -right-1/4 w-3/4 h-3/4 bg-brand-500/30 rounded-full blur-3xl mix-blend-screen" />
        <div className="absolute -bottom-1/3 -left-1/4 w-2/3 h-2/3 bg-cyan-500/25 rounded-full blur-3xl mix-blend-screen" />
        <div className="absolute inset-0 shadow-[inset_0_2px_30px_rgba(255,255,255,0.04),inset_0_-40px_60px_rgba(0,0,0,0.35)]" />
      </div>

      <div className="relative z-10 p-4 sm:p-5 space-y-3">
        {/* Eyebrow */}
        <div className="flex items-end justify-between gap-3">
          <div>
            {/* Eyebrow + title — paired light/dark tones (Task F).
                Light mode reads against the cyan/amber wash; dark
                mode preserves the original brand-300 + white look. */}
            <div className="text-[10px] sm:text-[11px] uppercase tracking-[.28em]
                            font-extrabold text-brand-700 dark:text-brand-300/95">
              Live rewards
            </div>
            <h3 className="font-display font-black text-lg sm:text-2xl leading-tight mt-0.5
                           text-arena-900 dark:text-white
                           dark:drop-shadow-[0_2px_8px_rgba(0,0,0,0.5)]">
              Sign up to claim
            </h3>
          </div>
          <Link
            to="/register"
            className="shrink-0 inline-flex items-center gap-1
                       h-10 px-4 sm:px-5 rounded-full
                       text-xs sm:text-sm font-extrabold uppercase tracking-wider text-white
                       bg-gradient-to-b from-brand-400 via-brand-500 to-brand-600
                       border border-white/20 active:scale-[.98] transition"
            style={{
              boxShadow: '0 10px 24px -10px rgba(240,126,51,0.7), inset 0 1px 0 rgba(255,255,255,0.3)',
            }}
          >
            {t('home.cta.signup') ?? 'Join now'}
            <Icon name="arrow_forward" size={16} filled />
          </Link>
        </div>

        {/* Horizontal scroll strip — overflow-x-auto with snap so a
            swipe lands cleanly on each tile. Tiles are square and
            sized for both mobile and desktop. */}
        <div className="-mx-4 sm:-mx-5 px-4 sm:px-5 overflow-x-auto no-scrollbar
                        snap-x snap-mandatory">
          <div className="flex gap-3 pb-1">
            {items.map((it) => (
              <div
                key={it.id}
                className="snap-start shrink-0 relative
                           w-24 h-24 sm:w-28 sm:h-28 rounded-2xl
                           grid place-items-center"
              >
                {/* Phase 16.5: per-tile cyan/orange halos removed.
                    The greeting card's page-level ambient backdrop
                    (lines ~158-159) is kept — that's surface wash on
                    the whole card, not artwork glow. The reward
                    image gets a single small black drop-shadow:
                    3px down, 4px blur, 20% opacity. */}
                <img
                  src={it.image_url}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  className="relative w-[85%] h-[85%] object-contain
                             drop-shadow-[0_3px_4px_rgba(0,0,0,0.20)]"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Tagline — paired light/dark tones (Task F). */}
        <div className="text-[10px] sm:text-[11px] uppercase tracking-[.2em]
                        text-arena-700 dark:text-white/55 text-center">
          High-value bonuses · cash back · streaks · daily prizes
        </div>
      </div>
    </section>
  );
}

function TrustRow() {
  const t = useT();
  const items: Array<{ icon: string; title: string; sub: string }> = [
    { icon: 'verified',  title: t('home.skillBased'),    sub: t('home.skillBasedSub') },
    { icon: 'shield',    title: t('home.fairPlay'),      sub: t('home.fairPlaySub') },
    { icon: 'bolt',      title: t('home.instantPayout'), sub: t('home.instantPayoutSub') },
  ];
  return (
    <section className="grid grid-cols-3 gap-2">
      {items.map((it) => (
        <div
          key={it.title}
          className="rounded-2xl p-3 text-center
                     bg-white/70 dark:bg-arena-800/50
                     border border-arena-200/60 dark:border-neon-400/15
                     backdrop-blur"
        >
          <div className="mx-auto w-9 h-9 rounded-xl grid place-items-center
                          bg-gradient-to-br from-neon-300/40 to-brand-300/30
                          dark:from-neon-400/20 dark:to-brand-500/20
                          text-arena-700 dark:text-neon-300">
            <Icon name={it.icon} size={20} filled />
          </div>
          <div className="mt-1.5 text-[11px] font-extrabold uppercase tracking-wider text-arena-800 dark:text-neon-100">
            {it.title}
          </div>
          <div className="text-[10px] text-arena-500 dark:text-neon-200/60">
            {it.sub}
          </div>
        </div>
      ))}
    </section>
  );
}

// Public-homepage game catalogue (logged-out users).
//
// 2026-05-22 — Now displays the FULL 15-card catalogue so prospective
// players see the whole platform offering on the marketing page. We
// fetch from /api/games (public, no auth) instead of the hardcoded
// frontend GAMES array, so live/locked toggles in the admin Launch
// Matrix automatically reflect on this surface too. No lock chips —
// logged-out visitors can't open any game anyway, so the goal here is
// pure catalogue visibility.
//
// Live games render with their real Logo.png artwork; the 10 "coming
// soon" cards render a vivid per-game gradient + emoji (each unique
// per migration 097) so no two tiles look the same.
type PublicGame = {
  id: string;
  displayName: string;
  themeGradient?: string | null;
  themeEmoji?: string | null;
  logoPath?: string | null;
  isLocked?: boolean;
  // Legacy shape from publicGameList() fallback.
  theme?: { gradient?: string; emoji?: string; logo?: string };
};

function GameShowcase({ loggedIn = false }: { loggedIn?: boolean }) {
  const [games, setGames] = useState<PublicGame[] | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetch('/api/games')
      .then((r) => r.json())
      .then((j) => {
        if (cancelled) return;
        const list = (j?.data?.games as PublicGame[] | undefined) || [];
        setGames(list);
      })
      .catch(() => { if (!cancelled) setGames([]); });
    return () => { cancelled = true; };
  }, []);

  if (games === null) {
    // Skeleton row while the public catalogue loads.
    return (
      <div className="grid grid-cols-3 gap-3 sm:gap-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="flex flex-col items-center">
            <div className="w-full aspect-square rounded-2xl bg-arena-200/60 dark:bg-arena-700/40 animate-pulse" />
            <div className="mt-2 h-3 w-2/3 rounded bg-arena-200/60 dark:bg-arena-700/40 animate-pulse" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-3 gap-3 sm:gap-4">
      {games.map((g) => {
        const gradient = g.themeGradient || g.theme?.gradient || 'from-arena-400 to-arena-700';
        const emoji = g.themeEmoji || g.theme?.emoji || '🎮';
        const logo = g.logoPath || g.theme?.logo || null;
        return (
          <Link
            key={g.id}
            to={loggedIn ? `/games/${g.id}` : '/login'}
            state={loggedIn ? undefined : { from: `/games/${g.id}` }}
            className="group flex flex-col items-center"
            aria-label={loggedIn ? `Play ${g.displayName}` : `Sign in to play ${g.displayName}`}
          >
            <div className="relative w-full aspect-square">
              <div
                className="absolute -inset-1 rounded-3xl opacity-60 group-hover:opacity-100 transition blur-md"
                style={{
                  background:
                    'radial-gradient(60% 60% at 30% 30%, rgba(143,255,254,.4), transparent 70%),' +
                    'radial-gradient(60% 60% at 70% 70%, rgba(240,126,51,.35), transparent 70%)',
                }}
                aria-hidden
              />
              <div className="relative w-full h-full p-[1.5px] rounded-2xl bg-gradient-to-br from-neon-400/70 via-arena-400/30 to-brand-400/70 group-active:scale-[.97] transition">
                <div className="relative w-full h-full rounded-[14px] overflow-hidden">
                  {/* Gradient base */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${gradient}`} aria-hidden />
                  {/* Logo artwork (shown on every tile, including
                      locked — dark veil below shades the locked ones) */}
                  {logo ? (
                    <img
                      src={logo}
                      alt=""
                      className="absolute inset-0 w-full h-full object-cover"
                      loading="lazy"
                      onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                    />
                  ) : (
                    <span aria-hidden className="absolute inset-0 grid place-items-center text-4xl drop-shadow">{emoji}</span>
                  )}
                  {/* No dark filter on the logged-out homepage —
                      every tile reads as a vibrant "live" game so
                      prospective players see a packed catalogue.
                      The lock state only surfaces AFTER sign-in, on
                      the in-app GameGrid where the shaded veil + 🔒
                      chip + "Unlock on Level 3" hint kick in. This
                      keeps the marketing surface aspirational. */}
                </div>
              </div>
            </div>
            <div className="mt-2 text-[11px] sm:text-xs font-display font-bold uppercase tracking-[0.18em] text-arena-700 dark:text-neon-200/90 text-center">
              {g.displayName}
            </div>
          </Link>
        );
      })}
    </div>
  );
}

function LoggedOutCTA() {
  const t = useT();
  return (
    <section className="relative overflow-hidden rounded-2xl border border-arena-200/60 dark:border-neon-400/20 shadow">
      <div
        className="absolute inset-0"
        style={{
          background:
            'linear-gradient(90deg, #F48D44 0%, #F07E33 60%, #CF671E 100%)',
        }}
        aria-hidden
      />
      <div className="relative px-4 py-3 flex items-center gap-3 text-white">
        <div className="text-2xl" aria-hidden>🏆</div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[.25em] font-bold opacity-80">{t('home.ready')}</div>
          <div className="font-display font-extrabold text-[15px] leading-tight">{t('home.dropIn')}</div>
        </div>
        <Link
          to="/register"
          className="shrink-0 inline-flex items-center gap-1 h-9 px-4 rounded-full
                     bg-white text-brand-600 text-xs font-extrabold uppercase tracking-wider
                     active:scale-[.98] transition"
        >
          {t('home.start')} <Icon name="arrow_forward" size={16} filled />
        </Link>
      </div>
    </section>
  );
}

/* ─── Logged-in blocks ──────────────────────────────────────────── */

// TOP GAMES hero — REPLACES the old wallet-balance card (owner request
// 2026-08-09). It reuses the wallet card's EXACT shell + theme-switching
// background (cyan ocean in light, warm orange in dark) so the surface reads
// identically; the balance now lives in the top-bar coin chip. Tiles are
// EMOJI-first (the same games show with full logos in the grid below). Matka
// King's "Coming soon" badge follows the lobby lock (2026-09-19, plan M-81):
// locked for this player → badge + toast; unlocked → a normal tile.
function TopGamesHero({ activeId }: { activeId?: string }) {
  const t = useT();
  const nav = useNavigate();
  const { push } = useToast();
  const { data: lobby } = useLobbyGames();
  const mk = lobby?.games.find((g) => g.id === 'matka_king');
  const matkaSoon = !!mk?.isLocked && !lobby?.myDevGrants.includes('matka_king');
  const TILES: { id: string; name: string; emoji: string; gradient: string; soon?: boolean; emojiClass?: string }[] = [
    { id: 'cricket',  name: 'Cricket',    emoji: '🏏', gradient: 'from-emerald-500 to-green-800' },
    { id: 'matka_king', name: 'Matka King', emoji: '🍯', gradient: 'from-amber-600 to-yellow-900', soon: matkaSoon },
    { id: 'football', name: 'Football',   emoji: '⚽', gradient: 'from-green-700 to-emerald-950' },
    { id: 'lucky7',   name: 'Lucky 7',    emoji: '❼', gradient: 'from-rose-600 to-red-900', emojiClass: 'text-white' },
    { id: 'tennis',   name: 'Tennis',     emoji: '🎾', gradient: 'from-lime-600 to-yellow-800' },
    { id: 'roulette', name: 'Roulette',   emoji: '🎡', gradient: 'from-emerald-700 to-red-900' },
  ];
  const tap = (g: (typeof TILES)[number]) => {
    if (g.soon) { push('info', `${g.name} — ${t('home.comingSoon')}`); return; }
    nav(`/games/${g.id}`);
  };
  return (
    <section className="relative overflow-hidden rounded-3xl
                        border border-arena-200/60 dark:border-brand-400/30
                        shadow-xl">
      {/* Phase R7 follow-up (2026-05-13): LIGHT mode now mirrors the
          LoggedOutHero's cyan ocean wash — same gradient stops, same
          halo treatment, same "orange CTA on cyan" feel. DARK mode is
          UNCHANGED — keeps the orange/peach surface the user
          explicitly likes. Background, halos, border, and the two
          button styles all carry per-theme variants.

          Why split (rather than ONE shared gradient): in dark mode
          the orange surface is recognised as the wallet affordance
          and reads warm/inviting. In light mode the cyan surface
          paired with the orange ADD button creates the same
          colour-coded CTA hierarchy as the marketing hero, which
          the user reported preferring. Text inside (navy on cyan,
          navy on orange) reads on both — no text-colour overrides
          per theme are needed. */}

      {/* Background — light: cyan ocean (matches LoggedOutHero light) */}
      <div
        className="block dark:hidden absolute inset-0"
        style={{
          background:
            'linear-gradient(135deg, #E0F7FA 0%, #8FFFFF 28%, #62F4F7 65%, #23D4D8 100%)',
        }}
        aria-hidden
      />
      {/* Background — dark: warm orange (preserved from the previous
          design — user explicitly wants dark mode left alone). */}
      <div
        className="hidden dark:block absolute inset-0"
        style={{
          background:
            'linear-gradient(135deg, #FFD9A8 0%, #F4A24A 35%, #F07E33 65%, #E07F1F 100%)',
        }}
        aria-hidden
      />

      {/* Top-right white highlight — works on both gradients (cyan
          mixes with screen-blended white, orange gets a subtle
          gloss). Identical in both modes. */}
      <div
        className="absolute -top-10 -right-10 w-44 h-44 rounded-full opacity-50 mix-blend-screen"
        style={{ background: 'radial-gradient(circle, rgba(255,255,255,.55), transparent 65%)' }}
        aria-hidden
      />
      {/* Bottom-left halo — light gets the LoggedOutHero's orange
          accent on cyan (warm × cool corner contrast). Dark keeps
          the previous white halo on orange. */}
      <div
        className="block dark:hidden absolute -bottom-14 -left-14 w-56 h-56 rounded-full opacity-50"
        style={{ background: 'radial-gradient(circle, rgba(240,126,51,.4), transparent 65%)' }}
        aria-hidden
      />
      <div
        className="hidden dark:block absolute -bottom-14 -left-14 w-56 h-56 rounded-full opacity-30"
        style={{ background: 'radial-gradient(circle, rgba(255,255,255,.4), transparent 65%)' }}
        aria-hidden
      />

      {/* Content — dark navy text reads on BOTH the cyan (light) and orange
          (dark) surfaces, exactly like the old balance card. */}
      <div className="relative px-4 py-3.5 sm:px-5 sm:py-4 text-arena-900 dark:text-orange-50">
        <div className="flex items-start justify-between">
          <div className="min-w-0">
            <div className="text-[10px] tracking-[.32em] uppercase font-extrabold text-arena-900">
              {t('home.resultOriented')}
            </div>
            <h2 className="mt-0.5 font-display font-black text-[26px] sm:text-[28px] leading-none tracking-tight text-arena-900 inline-flex items-center gap-2">
              {t('home.topGames')}
              <img
                src="/player/coin.png"
                alt=""
                aria-hidden
                draggable={false}
                className="w-6 h-6 sm:w-7 sm:h-7 drop-shadow-[0_2px_4px_rgba(0,0,0,0.35)]"
              />
            </h2>
          </div>
          {/* LIVE pill — same dark chip as the old wallet card. */}
          <span className="shrink-0 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-extrabold
                           px-2 py-0.5 rounded-full
                           bg-arena-900/15 dark:bg-arena-900/25
                           border border-arena-900/30
                           text-arena-900">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-600 animate-pulse" /> {t('home.live')}
          </span>
        </div>

        {/* The headline games as emoji app-icon tiles (6 since Roulette's
            launch, 2026-09-01 — the grid tracks TILES.length). */}
        <div className={`mt-2.5 sm:mt-3 grid gap-2 sm:gap-3 ${TILES.length === 6 ? 'grid-cols-6' : 'grid-cols-5'}`}>
          {TILES.map((g) => {
            // The slider below is showing this game right now — light it up and
            // point a caret down at the card so the two read as one unit.
            const active = g.id === activeId;
            return (
            <button
              key={g.id}
              type="button"
              onClick={() => tap(g)}
              aria-label={g.soon ? `${g.name} · ${t('home.comingSoon')}` : `Play ${g.name}`}
              aria-current={active ? 'true' : undefined}
              className="flex flex-col items-center gap-1 active:scale-[.96] transition"
            >
              {/* The game the slider is currently showing wears a THICK BLACK
                  border — the same black as the caret below it, so the "this
                  one" language is one colour and reads in every scenario, on
                  either theme, over any tile artwork. It is also slightly
                  bigger and gently breathes.
                  EVERY OTHER TILE IS LEFT COMPLETELY ALONE: same 0.5px accent
                  border it always had (light → orange, dark → cyan), same size,
                  full brightness. Never dim or fade the others (owner rule). */}
              <div className={`relative w-full aspect-square rounded-2xl overflow-hidden
                               bg-gradient-to-br ${g.gradient}
                               shadow-[0_6px_14px_-6px_rgba(0,0,0,.55)]
                               ${active
                                 ? 'z-10 border-[3px] border-black dark:border-white animate-tile-breathe motion-reduce:animate-none motion-reduce:scale-110'
                                 : 'border-[0.5px] border-[#F07E33] dark:border-[#23D4D8] transition-transform duration-300'}`}>
                <span className={`absolute inset-0 grid place-items-center text-[26px] sm:text-4xl drop-shadow ${g.emojiClass || ''}`} aria-hidden>
                  {g.emoji}
                </span>
                {g.soon && (
                  <span className="absolute bottom-0 inset-x-0 py-0.5 text-[6.5px] sm:text-[8px] font-bold uppercase tracking-wide
                                   text-center text-white bg-black/65">
                    {t('home.comingSoon')}
                  </span>
                )}
              </div>
              <span className={`w-full text-center text-[10px] sm:text-[11px] text-arena-900 leading-tight truncate
                                ${active ? 'font-black' : 'font-bold'}`}>
                {g.name}
              </span>

            </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/**
 * FeaturedBanner — fetches the player's active offers and either
 * renders the new OfferHero (when an offer with artwork is available)
 * or falls back to the original text-only "today's challenge" banner.
 *
 * The hero owns its own claim handling so the user can act on the
 * promoted offer right from the dashboard without bouncing to /rewards.
 * On success we refresh the global wallet store so the NavBar chip
 * updates immediately.
 */
/**
 * EarnRow — three ways to earn, in ONE slim row.
 *
 * Replaces the full-width "Today's challenge" banner (owner, 2026-08-11): same
 * job, a fraction of the height. Styled exactly like the logged-out TrustRow
 * cards (rounded-2xl, translucent surface, icon tile, tiny caps title) but
 * scaled down, because vertical space on the home screen is precious.
 *
 * One tile is ALWAYS the referral earner. Icon + a couple of words each — the
 * message lands without spending height. Every tile opens /rewards.
 */
function EarnRow() {
  const t = useT();
  const [refEarned, setRefEarned] = useState<number | null>(null);
  const [offerCount, setOfferCount] = useState<number | null>(null);

  useEffect(() => {
    let dead = false;
    api.get<{ stats?: { total_earned?: number } }>('/me/referral')
      .then((d) => { if (!dead) setRefEarned(Number(d?.stats?.total_earned ?? 0)); })
      .catch(() => {});
    api.get<{ offers: OfferLike[] }>('/me/offers')
      .then((r) => { if (!dead) setOfferCount((r.offers || []).length); })
      .catch(() => {});
    return () => { dead = true; };
  }, []);

  const inr = (n: number) => '\u20B9' + n.toLocaleString('en-IN');
  // ONE line per tile: icon + label, with the value folded in beside the label
  // instead of spending a whole second row on it. The two-row cards were
  // readable but ate too much height (owner, 2026-08-11).
  const items = [
    { icon: 'redeem',    label: t('home.todayChallenge'), value: offerCount ? String(offerCount) : null, to: '/rewards' },
    { icon: 'group_add', label: t('home.refer'),          value: refEarned && refEarned > 0 ? inr(refEarned) : null, to: '/rewards' },
    { icon: 'savings',   label: t('home.cashback'),       value: null, to: '/rewards' },
  ];

  return (
    <section className="grid grid-cols-3 gap-2">
      {items.map((it) => (
        <Link
          key={it.label}
          to={it.to}
          className="rounded-xl px-1.5 py-1.5 flex items-center justify-center gap-1
                     bg-white/70 dark:bg-arena-800/50
                     border border-arena-200/60 dark:border-neon-400/15
                     backdrop-blur active:scale-[.98] transition"
        >
          <Icon name={it.icon} size={14} filled
                className="shrink-0 text-arena-600 dark:text-neon-300" />
          {/* leading-tight, NEVER leading-none — Devanagari matras get clipped. */}
          <span className="text-[9.5px] font-extrabold uppercase tracking-wide leading-tight
                           text-arena-800 dark:text-neon-100">
            {it.label}
            {it.value && (
              <span className="ml-1 font-black normal-case text-brand-600 dark:text-brand-300">
                {it.value}
              </span>
            )}
          </span>
        </Link>
      ))}
    </section>
  );
}

function FeaturedBanner() {
  const t = useT();
  const { push } = useToast();
  const [offers, setOffers] = useState<OfferLike[] | null>(null);
  const [busyOfferId, setBusyOfferId] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get<{ offers: OfferLike[] }>('/me/offers');
        if (!cancelled) setOffers(r.offers);
      } catch {
        if (!cancelled) setOffers([]);    // fall through to fallback banner on error
      }
    })();
    return () => { cancelled = true; };
  }, []);

  async function claim(offerId: number) {
    setBusyOfferId(offerId);
    try {
      const r = await api.post<{ result: { state: string; amount?: number } | null }>(
        `/me/offers/${offerId}/claim`
      );
      const result = r.result;
      if (result?.state === 'credited') {
        push('success', t('rewards.toast.credited', { amount: '₹' + (result.amount ?? 0).toLocaleString('en-IN') }));
        await refreshWallet();
      } else if (result?.state === 'pending') {
        push('info', t('rewards.toast.submitted'));
      } else if (result?.state === 'duplicate' || !result) {
        push('info', t('rewards.toast.alreadyClaimed'));
      } else if (result?.state === 'rejected') {
        push('error', t('rewards.toast.rejected'));
      }
      // Re-fetch so the hero updates its state badge.
      const fresh = await api.get<{ offers: OfferLike[] }>('/me/offers');
      setOffers(fresh.offers);
    } catch (e) {
      push('error', (e as ApiError).message);
    } finally {
      setBusyOfferId(null);
    }
  }

  const hero = offers ? pickHeroOffer(offers) : null;

  // Hero variant — premium artwork-led featured offer.
  if (hero) {
    return (
      <OfferHero
        offer={hero}
        eyebrow={t('home.todayChallenge')}
        onClaim={
          hero.my_latest_claim?.state === 'credited'
            || hero.my_latest_claim?.state === 'pending'
            || hero.my_latest_claim?.state === 'approved'
            ? undefined
            : claim
        }
        busy={busyOfferId === hero.id}
      />
    );
  }

  // Fallback — original text banner. Identical visuals to pre-Phase-14.
  return (
    <section className="relative overflow-hidden rounded-2xl border border-arena-200/60 dark:border-neon-400/20 shadow">
      <div
        className="absolute inset-0"
        style={{
          background:
            'linear-gradient(90deg, #F48D44 0%, #F07E33 55%, #CF671E 100%)',
        }}
        aria-hidden
      />
      <div className="relative px-4 py-3 flex items-center gap-3 text-white">
        <div className="text-2xl" aria-hidden>🏆</div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[.25em] font-bold opacity-90">{t('home.todayChallenge')}</div>
          <div className="font-display font-extrabold text-[15px] leading-tight">{t('home.todayChallengeSub')}</div>
        </div>
        <Link
          to="/rewards"
          className="shrink-0 inline-flex items-center gap-1 h-9 px-4 rounded-full
                     bg-white text-brand-600 text-xs font-extrabold uppercase tracking-wider
                     active:scale-[.98] transition"
        >
          {t('home.view')} <Icon name="arrow_forward" size={16} filled />
        </Link>
      </div>
    </section>
  );
}

// Cricket hero — shows the branded cricket image for ~1s on EVERY visit, then
// (same card, same 16:9 size) cross-fades into a SCROLLABLE cricket match list
// with the live 1/X/2 rate rows (owner request 2026-08-09). Tapping a row opens
// that match; the header opens the full cricket lobby. Locked/empty → stays on
// the image with the Play-Now / Locked button.
function CricketHeroBanner() {
  const { data } = useLobbyGames();
  const nav = useNavigate();
  const [lockedModal, setLockedModal] = useState<LobbyGame | null>(null);

  const cricketGame = data?.games.find((g) => g.id === 'cricket');
  const hasDevGrant = data?.myDevGrants.includes('cricket');
  const locked = !!cricketGame?.isLocked && !hasDevGrant;

  const [showList, setShowList] = useState(false);
  const [matches, setMatches] = useState<any[]>([]);
  const [oddsState, setOddsState] = useState<Record<string, any>>({});

  // 1-second branded splash on every mount, then reveal the list.
  useEffect(() => {
    const t = setTimeout(() => setShowList(true), 1000);
    return () => clearTimeout(t);
  }, []);

  // Cricket match list (skip when the game is locked for this player).
  useEffect(() => {
    if (locked) return;
    let cancelled = false;
    api.get<{ success: boolean; matches: any[] }>('/cricket/matches?sport=cricket')
      .then((r) => { if (!cancelled) setMatches(r.matches || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [locked]);

  // Poll odds only while a cricket match is live.
  const hasLive = matches.some((m) => m.status === 'in-play');
  useEffect(() => {
    if (!hasLive) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await api.get<{ success: boolean; state: any }>('/cricket/state');
        if (!cancelled && r?.state) setOddsState(r.state);
      } catch { /* keep last */ }
    };
    poll();
    const timer = setInterval(poll, 2000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [hasLive]);

  function open(e: React.MouseEvent, id?: string) {
    e.preventDefault();
    if (locked) { setLockedModal(cricketGame!); return; }
    nav(id ? `/games/cricket/${id}` : '/games/cricket');
  }

  // Live first, then upcoming; completed dropped.
  const list = [
    ...matches.filter((m) => m.status === 'in-play'),
    ...matches.filter((m) => m.status === 'upcoming'),
  ];
  const listReady = showList && !locked && list.length > 0;

  return (
    <>
      <section
        className="relative overflow-hidden rounded-3xl border border-arena-200/60 dark:border-neon-400/20 shadow-xl aspect-[16/9]"
        style={{ backgroundColor: '#EADBB6' }}
      >
        {/* IMAGE splash (also the resting state when locked / no matches). */}
        <button
          type="button"
          onClick={(e) => open(e)}
          aria-label={locked ? 'Cricket (locked)' : 'Open Cricket'}
          className={`absolute inset-0 block text-left group transition-opacity duration-500
                      ${listReady ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
        >
          <img
            src="/cricket_hero.png"
            alt="Cricket Exchange"
            className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" aria-hidden />
          <div className="relative p-4 sm:p-5 flex flex-col justify-between h-full">
            <div className="flex justify-between items-start">
              <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-500/90 border border-red-400 text-[10px] font-extrabold uppercase tracking-widest text-white shadow-lg">
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> Live Exchange
              </div>
              {cricketGame?.isLocked && hasDevGrant && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-emerald-500/90 text-white shadow-lg ring-1 ring-inset ring-white/20">
                  <Icon name="verified_user" size={14} filled /> DEV
                </span>
              )}
            </div>
            <div className="mt-auto self-start">
              <div className="inline-flex items-center justify-center gap-1.5 h-9 px-5 rounded-full font-bold text-[12px] tracking-wider uppercase text-white bg-arena-900 border border-white/20 shadow-xl group-hover:bg-brand-600 transition-colors">
                <Icon name={locked ? 'lock' : 'sports_cricket'} size={16} filled />
                {locked ? 'Locked' : 'Play Now'}
              </div>
            </div>
          </div>
        </button>

        {/* LIST — scrollable cricket matches inside the SAME card. */}
        {!locked && (
          <div className={`absolute inset-0 flex flex-col bg-white dark:bg-arena-800 transition-opacity duration-500
                           ${listReady ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
            <button
              type="button"
              onClick={(e) => open(e)}
              className="shrink-0 flex items-center justify-between px-3 py-1.5 text-white bg-gradient-to-r from-red-600 to-brand-600"
            >
              <span className="inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-wider">
                <Icon name="sports_cricket" size={14} filled /> Cricket · Live Exchange
              </span>
              <span className="inline-flex items-center gap-0.5 text-[10px] font-bold uppercase tracking-wider">
                See all <Icon name="arrow_forward" size={12} />
              </span>
            </button>
            <div className="flex-1 min-h-0 overflow-y-auto px-2 py-1.5 space-y-1.5
                            [scrollbar-width:thin] [&::-webkit-scrollbar]:w-1.5">
              {list.map((m) => {
                const t1 = m.team_a || (m.title || '').replace(/ \([^)]+\)/g, '').split(' vs ')[0] || 'Team 1';
                const t2 = m.team_b || (m.title || '').replace(/ \([^)]+\)/g, '').split(' vs ')[1] || 'Team 2';
                const live = m.status === 'in-play';
                return (
                  <button
                    key={m.id}
                    type="button"
                    onClick={(e) => open(e, m.id)}
                    className="w-full text-left rounded-lg bg-arena-50 dark:bg-arena-700/50
                               border border-arena-200/60 dark:border-white/5 px-2.5 py-1.5
                               active:scale-[.99] transition"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-bold text-[12px] text-arena-900 dark:text-white truncate">
                        {t1} <span className="text-arena-400">v</span> {t2}
                      </span>
                      {live ? (
                        <span className="shrink-0 inline-flex items-center gap-1 text-[8.5px] font-black uppercase text-red-600 dark:text-red-400">
                          <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" /> Live
                        </span>
                      ) : (
                        <span className="shrink-0 text-[8.5px] font-bold uppercase tracking-wider text-arena-400 dark:text-neon-200/50">
                          {m.start_time ? new Date(m.start_time).toLocaleDateString([], { day: '2-digit', month: 'short' }) : 'TBC'}
                        </span>
                      )}
                    </div>
                    <LiveOddsRow state={oddsState[m.id]} team1={t1} team2={t2} />
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </section>
      {lockedModal && (
        <LockedGameModal game={lockedModal} onClose={() => setLockedModal(null)} />
      )}
    </>
  );
}

/* ─── Page ──────────────────────────────────────────────────────── */

export default function Home() {
  const { user } = useAuth();
  const t = useT();
  // Which game the hero carousel is currently showing — shared with the Top
  // Games tiles so they can highlight it and point down at the card. `dir` is
  // non-zero only when the PLAYER moved the slider (swipe / wheel / prev-next),
  // which is what makes the caret travel that way; `seq` re-triggers the
  // animation even when the same direction is used twice in a row.
  const [activeTopGame, setActiveTopGame] = useState('cricket');
  const [topNav, setTopNav] = useState({ dir: 0, seq: 0 });
  const handleTopActive = useCallback((id: string, dir = 0) => {
    setActiveTopGame(id);
    setTopNav((p) => ({ dir, seq: p.seq + 1 }));
  }, []);

  if (!user) {
    return (
      <div className="space-y-4">
        <LoggedOutHero />
        <TrustRow />
        <RewardsTeaserStrip />

        <CricketHeroBanner />

        <section>
          <div className="flex items-end justify-between mb-2 px-1">
            <h2 className="text-base font-display font-bold uppercase tracking-wider text-arena-800 dark:text-neon-100">
              {t('home.featured')}
            </h2>
            <span className="text-[10px] uppercase tracking-[.2em] text-arena-500 dark:text-neon-200/60">
              {t('home.tapToPlay')}
            </span>
          </div>
          <GameShowcase />
        </section>

        <LoggedOutCTA />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* TOP GAMES card — REPLACES the wallet-balance hero (owner 2026-08-09).
          Same card shell + theme-switching background, emoji tiles, big
          "TOP GAMES / RESULT ORIENTED". Balance now lives in the top-bar chip. */}
      {/* The tile card and the slider, tied together by ONE pointer that hangs
          BELOW the tile card and keeps nudging downward at the slider. Living
          outside the card is what lets it overhang — and is why the card no
          longer has to reserve any height for it (owner, 2026-08-11). */}
      <div className="relative">
        <TopGamesHero activeId={activeTopGame} />
        {/* Pointer row: SAME padding, SAME 5 columns, SAME gap as the tile
            grid, so the arrow is centred on its tile by construction rather
            than by a percentage guess. `top-full` puts it just under the card
            and the negative margin lets it overhang the edge. */}
        <div className="pointer-events-none absolute inset-x-0 top-full z-20 px-4 sm:px-5 -mt-1.5" aria-hidden>
          <div className={`grid gap-2 sm:gap-3 ${TOP_GAME_ORDER.length === 6 ? 'grid-cols-6' : 'grid-cols-5'}`}>
            {TOP_GAME_ORDER.map((id) => (
              <div key={id} className="flex justify-center">
                {id === activeTopGame && (
                  <span
                    key={topNav.dir ? `ptr-${topNav.seq}` : 'ptr'}
                    className={`block ${topNav.dir > 0 ? 'animate-caret-from-left'
                                : topNav.dir < 0 ? 'animate-caret-from-right' : ''}`}
                  >
                    {/* The arrow wears the TOP GAMES CARD's own colour, so it
                        reads as the card reaching down to its slider (owner,
                        2026-08-11): the card is cyan in light mode and orange in
                        dark, so the arrow is cyan/orange to match — each a shade
                        off the card so it still separates from the page behind
                        it (neon-700 #0EA3A8 on the pale page, brand-600 #CF671E
                        on the near-black one). The tile BORDER stays black/white
                        because it sits ON the coloured card, where a matching
                        colour would disappear. */}
                    <span className="block h-4 w-7 bg-neon-700 dark:bg-brand-600
                                     drop-shadow-[0_3px_4px_rgba(14,163,168,.45)] dark:drop-shadow-[0_3px_7px_rgba(207,103,30,.7)]
                                     animate-point-down motion-reduce:animate-none"
                          style={{ clipPath: 'polygon(50% 100%, 0 0, 100% 0)' }} />
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Top Games hero CAROUSEL (owner 2026-08-11) — the 5 top games
          rotate through ONE fixed card: 1s poster, then that game's live
          content (match list + odds / lucky7 results / matka COMING SOON),
          7s each, looping, with prev/next + a scroll nudge on every list.
          It reports the game on screen so the tiles above can point at it.
          Spec: docs/HOME_TOP_GAMES_CAROUSEL_SPEC.md */}
      <TopGamesCarousel onActiveChange={handleTopActive} />

      {/* Three ways to earn, in one slim row (replaces the tall banner). */}
      <EarnRow />

      {/* ALL the games — rendered EXACTLY like the logged-out home (the same
          GameShowcase grid of app-icon tiles), only here each tile PLAYS the
          game instead of routing to login (owner 2026-08-09). */}
      <section>
        <div className="flex items-end justify-between mb-2 px-1">
          <h2 className="text-base font-display font-bold uppercase tracking-wider text-arena-800 dark:text-neon-100">
            {t('home.featured')}
          </h2>
          <span className="text-[10px] uppercase tracking-[.2em] text-arena-500 dark:text-neon-200/60">
            {t('home.tapToPlay')}
          </span>
        </div>
        <GameShowcase loggedIn />
      </section>
    </div>
  );
}
